'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from 'react';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import PhoneInputWithCountrySelect, { Country } from 'react-phone-number-input';

// Import base styles from react-phone-number-input, then override flag styles
import 'react-phone-number-input/style.css';

import { getLogger, type Logger } from '@kit/shared/logger';
import { Label } from '@kit/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { toStoredPhoneValue } from './stored-phone-value';

// Custom styles to remove focus border from country selector button
// and ensure proper vertical alignment of flag emoji
const phoneInputStyles = `
  /* Remove all focus styles from the main PhoneInput container */
  .PhoneInput,
  .PhoneInput:focus,
  .PhoneInput:focus-within,
  .PhoneInput:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }

  /* Remove ALL focus styles from country selector and its children */
  .PhoneInputCountry,
  .PhoneInputCountry:focus,
  .PhoneInputCountry:focus-visible,
  .PhoneInputCountry:focus-within,
  .PhoneInputCountrySelect,
  .PhoneInputCountrySelect:focus,
  .PhoneInputCountrySelect:focus-visible,
  .PhoneInputCountrySelect:focus-within,
  .PhoneInputCountrySelectArrow,
  .PhoneInputCountrySelectArrow:focus,
  .PhoneInputCountryIcon,
  .PhoneInputCountryIcon:focus,
  .PhoneInputCountryIcon--border,
  .PhoneInputCountryIcon--border:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: transparent !important;
  }

  /* Hide the default focus ring on the hidden select */
  .PhoneInputCountrySelect {
    outline: none !important;
    box-shadow: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
  }

  /* Remove the border that appears on focus for the icon wrapper */
  .PhoneInputCountryIcon--border {
    background-color: transparent !important;
    box-shadow: none !important;
  }

  /* Ensure country selector container is properly aligned */
  .PhoneInputCountry {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
  }

  /* Ensure flag icon is vertically centered */
  .PhoneInputCountryIcon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    box-shadow: none !important;
  }
`;

// Inject styles into document head (deferred to useEffect to avoid SSR errors)
function injectPhoneInputStyles() {
  if (typeof document === 'undefined') return;

  const styleId = 'phone-input-custom-styles';
  const existingStyle = document.getElementById(styleId);

  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = phoneInputStyles;
    if (document.head) {
      document.head.appendChild(style);
    }
  }
}

/**
 * Converts a two-letter country code to its flag emoji
 * Uses Regional Indicator Symbols: 'A'-'Z' maps to U+1F1E6-U+1F1FF
 * @param countryCode - Two-letter ISO 3166-1 alpha-2 country code
 * @returns Flag emoji string (e.g., 'CH' → '🇨🇭', 'US' → '🇺🇸')
 */
const countryToFlagEmoji = (countryCode: string): string => {
  const REGIONAL_INDICATOR_OFFSET = 127397; // 0x1F1E6 - 65 ('A')
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) =>
      String.fromCodePoint(char.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET),
    )
    .join('');
};

/**
 * Custom Flag component that renders emoji flags instead of SVG images.
 * This component is passed to PhoneInputWithCountrySelect via the flagComponent prop
 * to replace the default image-based flags with emoji flags.
 *
 * Matches the FlagProps interface from react-phone-number-input:
 * - country: Country (two-letter code)
 * - countryName: string (display name)
 * - flagUrl?: string (ignored - we use emojis)
 * - flags?: Flags (ignored - we use emojis)
 *
 * @example
 * <FlagEmoji country="CH" countryName="Switzerland" /> // Renders 🇨🇭
 */
const FlagEmoji = ({
  country,
  countryName,
}: {
  country: string;
  countryName: string;
  flagUrl?: string;
  flags?: Record<string, unknown>;
}) => {
  const { t } = useTranslation();

  if (!country) {
    return (
      <span
        className="PhoneInputCountryIcon flex items-center justify-center"
        aria-label={t('dancer:phoneInput.noCountrySelected')}
        role="img"
        style={{ fontSize: '1.25rem', lineHeight: 1, height: '100%' }}
      >
        🌍
      </span>
    );
  }

  return (
    <span
      className="PhoneInputCountryIcon flex items-center justify-center"
      role="img"
      aria-label={
        countryName || t('dancer:phoneInput.countryFlag', { country })
      }
      title={countryName || country}
      style={{ fontSize: '1.25rem', lineHeight: 1, height: '100%' }}
    >
      {countryToFlagEmoji(country)}
    </span>
  );
};

export interface PhoneData {
  fullNumber: string;
  countryCode?: string;
  nationalNumber?: string;
  country?: string;
  isValid: boolean;
  internationalFormat?: string;
}

/**
 * Derives display metadata for an E.164 value (or none).
 */
function derivePhoneData(e164: string | undefined): PhoneData {
  if (e164 && isPossiblePhoneNumber(e164)) {
    try {
      const parsed = parsePhoneNumber(e164);
      return {
        fullNumber: e164,
        countryCode: parsed?.countryCallingCode,
        nationalNumber: parsed?.nationalNumber,
        country: parsed?.country,
        isValid: true,
        internationalFormat: parsed?.formatInternational(),
      };
    } catch (error) {
      void getLogger().then((logger: Logger) =>
        logger.error({ error }, 'Failed to parse initial phone number'),
      );
    }
  }
  return {
    fullNumber: e164 ?? '',
    isValid: false,
  };
}

export type WhatsAppStatus = 'checking' | 'whatsapp' | 'no-whatsapp' | null;

const WHATSAPP_ICON_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

export interface PhoneInputProps {
  value?: string;
  onChange?: (phone: string, metadata: PhoneData) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  defaultCountry?: Country;
  id?: string;
  name?: string;
  /** WhatsApp registration status indicator */
  whatsappStatus?: WhatsAppStatus;
}

// Static PhoneInputField component to prevent focus loss
const PhoneInputField = forwardRef<
  HTMLInputElement,
  ComponentPropsWithoutRef<'input'>
>((props, inputRef) => (
  // eslint-disable-next-line kit-components/use-kit-primitives -- deliberately unstyled inner field injected as react-phone-number-input's inputComponent; @kit/ui Input chrome (border, h-9, px-3) would break the flag+field wrapper layout
  <input
    {...props}
    ref={inputRef}
    className={cn(
      'min-w-0 flex-1 border-none bg-transparent outline-none',
      'text-foreground ml-1 px-0 text-sm sm:ml-2',
      'placeholder:text-muted-foreground',
      'disabled:cursor-not-allowed disabled:opacity-50',
    )}
  />
));

PhoneInputField.displayName = 'PhoneInputField';

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      onBlur,
      disabled = false,
      placeholder,
      className,
      label,
      hint,
      error,
      required = false,
      defaultCountry = 'CH',
      id,
      name,
      whatsappStatus,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const resolvedPlaceholder =
      placeholder ?? t('dancer:phoneInput.placeholder');
    // The library accepts only E.164 and logs console.error otherwise
    // (Sentry BALLEE-JJ). Stored numbers are often loosely formatted or
    // national format, so normalise here, at the shared boundary, for every
    // consumer.
    const [initialStored] = useState(() => toStoredPhoneValue(value));
    const [phoneValue, setPhoneValue] = useState<string | undefined>(
      initialStored.value,
    );
    // A stored number that cannot be read as E.164 is kept out of the
    // library but shown to the user, never silently dropped.
    const [unrecognizedValue, setUnrecognizedValue] = useState<
      string | undefined
    >(initialStored.unrecognized);
    const [phoneData, setPhoneData] = useState<PhoneData>(() =>
      derivePhoneData(initialStored.value),
    );
    const phoneValueRef = useRef(phoneValue);
    phoneValueRef.current = phoneValue;

    // Inject custom styles on mount (client-side only)
    useEffect(() => {
      injectPhoneInputStyles();
    }, []);

    // Synchronize external value changes (only when external value changes).
    // The ref reads the current input without depending on it, so a keystroke
    // (which sets phoneValue but not the external `value`) never re-triggers
    // this effect and reverts the user's in-progress input.
    useEffect(() => {
      const next = toStoredPhoneValue(value);
      if (next.value !== phoneValueRef.current) {
        setPhoneValue(next.value);
        // Keep validity in step, or a number loaded after mount would show
        // the invalid hint.
        setPhoneData(derivePhoneData(next.value));
      }
      setUnrecognizedValue(next.unrecognized);
    }, [value]);

    const handlePhoneChange = useCallback(
      (newValue: string | undefined) => {
        // Max phone length (E.164 max is 15 digits + 1 for + = 16, but allow buffer for formatting)
        const MAX_PHONE_LENGTH = 20;
        if (newValue && newValue.length > MAX_PHONE_LENGTH) {
          return; // Ignore excessive input
        }

        let correctedValue = newValue;

        // Auto-correct double prefixes like +41+41 -> +41
        // Handles patterns: +41+41, +41+4176, +1+16505551234, etc.
        if (correctedValue && correctedValue.startsWith('+')) {
          correctedValue = correctedValue.replace(/^(\+\d+)\++(\d.*)$/, '$1$2');
        }

        setPhoneValue(correctedValue);
        // Typing replaces an unrecognized stored number.
        setUnrecognizedValue(undefined);

        if (!correctedValue) {
          const data: PhoneData = {
            fullNumber: '',
            isValid: false,
          };
          setPhoneData(data);
          onChange?.('', data);
          return;
        }

        let data: PhoneData;

        try {
          const isValid = isPossiblePhoneNumber(correctedValue);

          if (isValid) {
            const parsed = parsePhoneNumber(correctedValue);
            data = {
              fullNumber: correctedValue,
              countryCode: parsed?.countryCallingCode,
              nationalNumber: parsed?.nationalNumber,
              country: parsed?.country,
              isValid: true,
              internationalFormat: parsed?.formatInternational(),
            };
          } else {
            data = {
              fullNumber: correctedValue,
              isValid: false,
            };
          }
        } catch (error) {
          void getLogger().then((logger: Logger) =>
            logger.error(
              { error, phone: correctedValue },
              'Failed to parse phone number',
            ),
          );
          data = {
            fullNumber: correctedValue,
            isValid: false,
          };
        }

        setPhoneData(data);
        onChange?.(correctedValue, data);
      },
      [onChange],
    ); // Removed phoneValue dependency to prevent unnecessary re-renders

    const errorId = error ? `${id}-error` : undefined;
    const showsUnrecognizedValue = !phoneValue && !!unrecognizedValue;
    const isInvalid =
      (!!phoneValue && !phoneData.isValid) || showsUnrecognizedValue;
    const descriptionId = !error && isInvalid ? `${id}-description` : undefined;

    // Hold the latest dynamic aria values in a ref so the memoized input
    // component can read them without being recreated. Recreating the
    // component type on each keystroke would remount the input element and lose
    // focus (the whole reason InputComponent is memoized). The inner
    // component re-renders on every value change (controlled input), so the
    // ref always yields fresh aria attributes.
    const ariaPropsRef = useRef({
      invalid: !!error || isInvalid,
      describedBy: errorId || descriptionId,
    });
    ariaPropsRef.current = {
      invalid: !!error || isInvalid,
      describedBy: errorId || descriptionId,
    };

    // Create stable input component - only depends on truly static values
    const InputComponent = useMemo(() => {
      const PhoneInputInner = forwardRef<
        HTMLInputElement,
        ComponentPropsWithoutRef<'input'>
      >((props, inputRef) => (
        <PhoneInputField
          {...props}
          ref={inputRef || ref}
          id={id}
          name={name}
          aria-invalid={ariaPropsRef.current.invalid}
          aria-describedby={ariaPropsRef.current.describedBy}
        />
      ));
      PhoneInputInner.displayName = 'PhoneInputInner';
      return PhoneInputInner;
    }, [id, name, ref]); // Only truly stable dependencies

    return (
      <div className="space-y-2">
        {label && (
          <div className="space-y-1">
            <Label htmlFor={id} className={cn(required && 'required')}>
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {hint && !error && (
              <p className="text-muted-foreground text-sm">{hint}</p>
            )}
          </div>
        )}

        <div className={cn('relative w-full', className)}>
          <div className="flex items-center gap-2">
            <PhoneInputWithCountrySelect
              international
              defaultCountry={defaultCountry}
              value={phoneValue}
              onChange={handlePhoneChange}
              onBlur={onBlur}
              disabled={disabled}
              placeholder={resolvedPlaceholder}
              flagComponent={FlagEmoji}
              inputComponent={InputComponent}
              className={cn(
                'border-input flex h-11 w-full items-center rounded-md border',
                'bg-background ring-offset-background px-3 text-sm',
                'transition-colors duration-200',
                'focus-within:ring-ring focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none',
                disabled && 'cursor-not-allowed opacity-50',
                error && 'border-destructive focus-within:ring-destructive',
                !error &&
                  isInvalid &&
                  'border-warning focus-within:ring-warning',
              )}
            />
            <WhatsAppStatusIndicator status={whatsappStatus} />
          </div>
        </div>

        {error && (
          <p id={errorId} className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}

        {!error && isInvalid && (
          <p id={descriptionId} className="text-muted-foreground text-sm">
            {showsUnrecognizedValue ? (
              <Trans
                i18nKey="common:validation.unrecognizedPhoneNumber"
                defaults="The saved number {{value}} could not be read. Please re-enter it with its country code."
                values={{ value: unrecognizedValue }}
              />
            ) : (
              <Trans i18nKey="common:validation.invalidPhoneNumber">
                Please enter a valid phone number
              </Trans>
            )}
          </p>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

/**
 * WhatsApp status indicator shown next to the phone input.
 * Shows a spinner while checking, a green check for WhatsApp numbers,
 * or an orange warning for non-WhatsApp numbers.
 */
function WhatsAppStatusIndicator({ status }: { status?: WhatsAppStatus }) {
  const { t } = useTranslation();

  if (!status) return null;

  if (status === 'checking') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center">
        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (status === 'whatsapp') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5 text-green-500"
                fill="currentColor"
                aria-label={t('dancer:phoneInput.whatsappRegistered')}
              >
                <path d={WHATSAPP_ICON_PATH} />
              </svg>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <Trans
              i18nKey="common:whatsapp.registered"
              defaults="This number is on WhatsApp"
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (status === 'no-whatsapp') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5 text-orange-400"
                fill="currentColor"
                aria-label={t('dancer:phoneInput.notOnWhatsapp')}
              >
                <path d={WHATSAPP_ICON_PATH} />
              </svg>
              <div className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-orange-400" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <Trans
              i18nKey="common:whatsapp.notRegistered"
              defaults="This number is not on WhatsApp"
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}
